import React, { Fragment, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { BoundedContent, Divider, getTokenCssVar, Ul } from '@vp/swan'
import type { NavigationCategory, NavigationItem } from '../../../../types/navigation'
import { layoutNavColumns } from './layout-utils'
import { SubCategory } from './SubCategory'
import { PromotionalTile } from './PromotionalTile'
import { desktopNavContainer } from './DesktopNavigationItem.scss'
import Veil from './Veil'
import { useNavContext } from '../../../../context/NavContextProvider'
import SeeAllLink from './SeeAllLink'

interface DesktopNavigationL2Props {
  readonly data: NavigationCategory
  readonly onClose: () => void
  readonly isActive?: boolean
}

/**
 * Desktop navigation level-2 flyout panel.
 * Shows subcategories in a multi-column layout with visual tiles and MX sections.
 */
export const DesktopNavigationL2: React.FC<DesktopNavigationL2Props> = memo(({ data, onClose, isActive }) => {
  const submenuRef = useRef<HTMLUListElement>(null)
  const [veilTop, setVeilTop] = useState(0)
  const { isMxNav } = useNavContext()

  // Measure the bottom edge of the submenu so the Veil starts right below it.
  // Runs after DOM commit but before paint, ensuring submenuRef is available.
  // Also updates on scroll/resize so the veil stays anchored to the flyout
  // when the page scrolls or the viewport changes size.
  useLayoutEffect(() => {
    if (!isActive || !submenuRef.current) return

    const updateVeilTop = () => {
      if (submenuRef.current) {
        setVeilTop(submenuRef.current.getBoundingClientRect().bottom)
      }
    }

    updateVeilTop()

    window.addEventListener('scroll', updateVeilTop, { passive: true })
    window.addEventListener('resize', updateVeilTop)

    const resizeObserver = new ResizeObserver(updateVeilTop)
    resizeObserver.observe(submenuRef.current)

    return () => {
      window.removeEventListener('scroll', updateVeilTop)
      window.removeEventListener('resize', updateVeilTop)
      resizeObserver.disconnect()
    }
  }, [isActive])
  // Auto-focus the first submenu link when the flyout was opened via keyboard.
  // The caret trigger retains focus during a keyboard-triggered open, so we detect
  // that by checking if it is the active element when isActive becomes true.
  useEffect(() => {
    if (!isActive) return
    const triggerButton = document.getElementById(`${data.id}-button`)
    if (triggerButton && document.activeElement === triggerButton) {
      const submenu = document.getElementById(`${data.id}-submenu`)
      submenu?.querySelector<HTMLAnchorElement>('a')?.focus()
    }
  }, [data.id, isActive])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const closeAndFocusTrigger = () => {
        onClose()
        const trigger = document.getElementById(`${data.id}-button`)
        delete trigger?.dataset['keyboardNav']
        trigger?.focus()
      }

      if (event.key === 'Escape') {
        closeAndFocusTrigger()
        return
      }

      if (event.key !== 'Tab') return

      const submenu = document.getElementById(`${data.id}-submenu`)
      const focusableElements = submenu?.querySelectorAll<HTMLElement>('a, button')
      if (!focusableElements || focusableElements.length === 0) return

      const isOnLastElement = document.activeElement === focusableElements[focusableElements.length - 1] && !event.shiftKey
      const isOnFirstElement = document.activeElement === focusableElements[0] && event.shiftKey

      if (isOnLastElement || isOnFirstElement) {
        event.preventDefault()
        closeAndFocusTrigger()
      }
    },
    [data.id, onClose],
  )

  const columns = useMemo(() => layoutNavColumns(data), [data])

  return (
    <>
      <Ul
        ref={submenuRef}
        onKeyDown={handleKeyDown}
        aria-label={`${data.text} submenu`}
        id={`${data.id}-submenu`}
        skin="minimal"
        role="menu"
        backgroundColor="standard"
        display={isActive ? 'flex' : 'none'}
        className={desktopNavContainer}
        pt={0}
      >
        <BoundedContent
          as="li"
          py={7}
          px={8}
          display="flex"
          style={{ justifyContent: data.isVisual ? 'flex-start' : isMxNav ? 'center' : 'space-between', width: '100%' }}
        >
          {columns.map((column, index) => (
            <DesktopNavigationL2Item key={column[0]!.id} column={column} data={data} isLastColumn={columns.length === index + 1} />
          ))}
        </BoundedContent>
        {!isMxNav && (
          <SeeAllLink
            seeAllText={data.seeAllCategory}
            seeAllUrl={data.url}
            parentNavigationDetailPath={[data.analyticsId || data.text]}
            seeAllAnalyticsId={data.analyticsId}
          />
        )}
      </Ul>
      <Veil show={!!isActive} onClose={onClose} top={veilTop} />
    </>
  )
})

type DesktopNavigationL2ItemProps = {
  readonly column: ReturnType<typeof layoutNavColumns>[number]
  readonly data: NavigationCategory
  readonly isLastColumn: boolean
}

const DesktopNavigationL2Item = ({ column, data, isLastColumn }: DesktopNavigationL2ItemProps) => {
  if (column.length === 0) return null

  const startOfMx = column.find((item) => item.startOfMx)
  const isVisualColumn = Boolean(column.find((item) => item.isVisual) || data.isVisual)

  return (
    <Fragment key={`col-${column[0]!.id}`}>
      {startOfMx && <Divider style={{ height: 'unset', width: '1px', marginRight: getTokenCssVar('SwanSemSpace6') }} />}
      <Ul
        key={`col-${column[0]!.id}`}
        skin="minimal"
        style={{
          width: isVisualColumn ? '15.5%' : 'auto',
          maxWidth: '15.5%',
          gap: getTokenCssVar('SwanSemSpaceBetweenSubsections'),
          marginRight: isLastColumn ? 0 : '4%',
        }}
        pt={0}
      >
        {column.map((entry: NavigationItem) =>
          isVisualColumn || entry.isVisual ? (
            <PromotionalTile key={entry.id} navItem={entry} parentText={data.analyticsId} isVisualCategory={!!data.isVisual} />
          ) : (
            <SubCategory key={entry.id} navItem={entry} parent={data} />
          ),
        )}
      </Ul>
    </Fragment>
  )
}
